import { useCallback } from 'react';

import { useLatestValueRef } from 'modules/dragAndDropEditor/hooks';
import { useEditableFieldsContext } from '../context/EditableFieldsContext';
import { useEditSavedRowDesignContext } from '../context/EditSavedRowDesignContext';

/**
 * Returns change handlers for the content editors.
 */
export const useContentEditorChangeHandlers = () => {
  const { setCurrentMediumFields } = useEditableFieldsContext();
  const { setBeeBuilderHTML, setPageBeeBuilderMetadata, isEditingSavedRowDesignRef } =
    useEditSavedRowDesignContext();

  const onHtmlChange = useCallback(
    (newHtml: string) => {
      if (isEditingSavedRowDesignRef.current) {
        setBeeBuilderHTML(newHtml);
      } else {
        setCurrentMediumFields({ html: newHtml });
      }
    },
    [isEditingSavedRowDesignRef, setBeeBuilderHTML, setCurrentMediumFields],
  );

  const onJsonChange = useCallback(
    (newBeeBuilderMetadata: string) => {
      if (isEditingSavedRowDesignRef.current) {
        setPageBeeBuilderMetadata(newBeeBuilderMetadata);
      } else {
        setCurrentMediumFields({ beeBuilderMetadata: newBeeBuilderMetadata });
      }
    },
    [isEditingSavedRowDesignRef, setPageBeeBuilderMetadata, setCurrentMediumFields],
  );

  const onAmpChange = useCallback(
    (ampHtml: string) => setCurrentMediumFields({ ampHtml }),
    [setCurrentMediumFields],
  );

  const onTextChange = useCallback(
    (plainText: string) => setCurrentMediumFields({ plainText }),
    [setCurrentMediumFields],
  );

  const onAmpChangeRef = useLatestValueRef(onAmpChange);
  const onTextChangeRef = useLatestValueRef(onTextChange);
  const onHtmlChangeRef = useLatestValueRef(onHtmlChange);
  const onJsonChangeRef = useLatestValueRef(onJsonChange);

  return { onHtmlChangeRef, onJsonChangeRef, onAmpChangeRef, onTextChangeRef };
};
